import { useState, useCallback } from "react";
import { MessageSquarePlus, Bug, Lightbulb, TrendingUp, MessageCircle, Upload, X, Image, FileText } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { logger } from "@/utils/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { optimizeImage } from "@/utils/imageOptimization";

const feedbackTypes = [
  { value: "bug", label: "Bug Report", icon: Bug },
  { value: "feature", label: "Feature Request", icon: Lightbulb },
  { value: "improvement", label: "Improvement", icon: TrendingUp },
  { value: "other", label: "Other", icon: MessageCircle },
];

const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

interface UploadedFile {
  file: File;
  preview?: string;
}

interface PlatformFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlatformFeedbackDialog({ open, onOpenChange }: PlatformFeedbackDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    feedback_type: "",
    title: "",
    description: "",
  });
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const resetForm = () => {
    setFormData({ feedback_type: "", title: "", description: "" });
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
  };

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const validFiles: UploadedFile[] = [];

    for (const file of fileArray) {
      if (files.length + validFiles.length >= MAX_FILES) {
        toast({
          title: "Too many files",
          description: `Maximum ${MAX_FILES} files allowed.`,
          variant: "destructive",
        });
        break;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit.`,
          variant: "destructive",
        });
        continue;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a supported file type.`,
          variant: "destructive",
        });
        continue;
      }

      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined;

      validFiles.push({ file, preview });
    }

    setFiles((prev) => [...prev, ...validFiles]);
  }, [files.length, toast]);

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const removed = newFiles.splice(index, 1)[0];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return newFiles;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const uploadFiles = async (feedbackId: string, userId: string) => {
    const attachments = [];

    for (const { file } of files) {
      try {
        let uploadFile = file;

        // Optimize images before upload
        if (file.type.startsWith("image/")) {
          const optimized = await optimizeImage(file, {
            maxWidth: 1920,
            maxHeight: 1920,
            quality: 0.85,
          });
          uploadFile = new File([optimized.blob], file.name, {
            type: optimized.format,
          });
        }

        const filePath = `${userId}/${feedbackId}/${Date.now()}_${file.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from("platform-feedback-attachments")
          .upload(filePath, uploadFile);

        if (uploadError) throw uploadError;

        attachments.push({
          feedback_id: feedbackId,
          user_id: userId,
          file_name: file.name,
          file_path: filePath,
          file_size: uploadFile.size,
          mime_type: uploadFile.type,
        });
      } catch (error) {
        logger.error("Error uploading file", error);
      }
    }

    if (attachments.length > 0) {
      const { error } = await supabase
        .from("platform_feedback_attachments")
        .insert(attachments);

      if (error) {
        logger.error("Error saving attachment records", error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit feedback.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.feedback_type || !formData.title || !formData.description) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("platform_feedback")
        .insert({
          user_id: user.id,
          feedback_type: formData.feedback_type,
          title: formData.title,
          description: formData.description,
        })
        .select("id")
        .single();

      if (error) throw error;

      // Upload files if any
      if (files.length > 0 && data?.id) {
        await uploadFiles(data.id, user.id);
      }

      toast({
        title: "Thank you!",
        description: "Your feedback helps us improve Sendfeedback.",
      });

      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      logger.error("Error submitting platform feedback", error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isImageFile = (mimeType: string) => mimeType.startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5" />
            Help Us Improve
          </DialogTitle>
          <DialogDescription>
            Share your thoughts about Sendfeedback. Your feedback helps us build a better platform.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="feedback-type">Feedback Type</Label>
            <Select
              value={formData.feedback_type}
              onValueChange={(value) =>
                setFormData({ ...formData, feedback_type: value })
              }
            >
              <SelectTrigger id="feedback-type">
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent>
                {feedbackTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Brief summary of your feedback"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Tell us more about your feedback..."
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground">
              {formData.description.length}/1000 characters
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Attachments (optional)</Label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept={ALLOWED_TYPES.join(",")}
                className="hidden"
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
              />
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Drag & drop files or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max {MAX_FILES} files, 10MB each. Images, PDFs, documents.
              </p>
            </div>

            {/* File Previews */}
            {files.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {files.map((f, fileIndex) => (
                  <div
                    key={f.file.name}
                    className="relative group rounded-lg border bg-muted/50 overflow-hidden"
                  >
                    {isImageFile(f.file.type) && f.preview ? (
                      <img
                        src={f.preview}
                        alt={f.file.name}
                        className="w-full h-20 object-cover"
                      />
                    ) : (
                      <div className="w-full h-20 flex flex-col items-center justify-center p-2">
                        <FileText className="h-6 w-6 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground truncate w-full text-center mt-1">
                          {f.file.name}
                        </p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(fileIndex);
                      }}
                      className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <SecondaryButton
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </PrimaryButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
